import { useMemo } from "react";

import dayjs from "dayjs";

import { SCHOOL_YEAR_2026_2027 } from "../../../mockData/schoolYearData";
import {
  enrichTimelineProject,
  getSubjectCategory,
} from "../utils/timelineUtils";

const CATEGORY_ORDER = [
  "Sprachen",
  "Naturwissenschaften",
  "Geisteswissenschaften",
  "Wirtschaft",
  "Kunst & Sport",
  "Sonstiges",
];

export default function usePlannerTimelineData({
  projects = [],
  subjects = [],
  isTeacher = true,
  selectedClass = null,
}) {
  return useMemo(() => {
    const start = dayjs(SCHOOL_YEAR_2026_2027.start).startOf("isoWeek");

    const end = dayjs(SCHOOL_YEAR_2026_2027.end).endOf("isoWeek");

    const weeks = [];

    let current = start;

    while (current.isBefore(end) || current.isSame(end, "day")) {
      weeks.push(current);
      current = current.add(1, "week");
    }

    const holidayWeeks = {};

    (SCHOOL_YEAR_2026_2027.holidays || []).forEach((holiday) => {
      let currentHolidayWeek = dayjs(holiday.start).startOf("isoWeek");

      const holidayEnd = dayjs(holiday.end).endOf("isoWeek");

      while (
        currentHolidayWeek.isBefore(holidayEnd) ||
        currentHolidayWeek.isSame(holidayEnd, "day")
      ) {
        holidayWeeks[currentHolidayWeek.format("YYYY-MM-DD")] = holiday;

        currentHolidayWeek = currentHolidayWeek.add(1, "week");
      }
    });

    const visibleProjects = selectedClass
      ? projects.filter((project) =>
          (project.classes || []).some((cls) => cls.id === selectedClass.id),
        )
      : projects;

    const subjectsMap = {};

    if (isTeacher) {
      const filteredSubjects = selectedClass?.grade
        ? subjects.filter((subject) =>
            (subject.grades || []).some((g) => g.grade === selectedClass.grade),
          )
        : subjects;

      filteredSubjects.forEach((subject) => {
        const subjectName =
          subject.subjectName ?? subject.name ?? subject.shortName;

        if (!subjectName) {
          return;
        }

        subjectsMap[subjectName] = {
          subjectId: subject.id,
          subjectName,

          classId: selectedClass?.id ?? null,
          className: selectedClass?.name ?? null,

          projects: [],
        };
      });
    }

    visibleProjects.forEach((project) => {
      if (!subjectsMap[project.subjectName]) {
        subjectsMap[project.subjectName] = {
          subjectId: project.subjectId,
          subjectName: project.subjectName,

          classId: selectedClass?.id ?? null,
          className: selectedClass?.name ?? null,

          projects: [],
        };
      }

      subjectsMap[project.subjectName].projects.push(
        enrichTimelineProject(project, start),
      );
    });

    let subjectRows = Object.values(subjectsMap);

    if (!isTeacher) {
      subjectRows = subjectRows.filter(
        (subject) => subject.projects.length > 0,
      );
    }

    subjectRows.sort((a, b) => {
      const categoryA = getSubjectCategory(a.subjectName);

      const categoryB = getSubjectCategory(b.subjectName);

      const indexA = CATEGORY_ORDER.indexOf(categoryA);

      const indexB = CATEGORY_ORDER.indexOf(categoryB);

      if (indexA !== indexB) {
        return indexA - indexB;
      }

      return a.subjectName.localeCompare(b.subjectName, "de");
    });

    const currentWeekIndex = weeks.findIndex((week) =>
      dayjs().isSame(week, "week"),
    );

    return {
      weeks,
      holidayWeeks,
      subjectRows,
      currentWeekIndex,
    };
  }, [projects, subjects, isTeacher, selectedClass]);
}
